import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Shield, AlertTriangle } from 'lucide-react';
import { CodeBlock } from '../components/code/CodeBlock';

const GatewayCheckoutHome: React.FC = () => {
  const paymentMethods = [
    { method: 'Opay', status: 'Available', available: true },
    { method: 'PalmPay', status: 'Available', available: true }
  ];

  const environments = [
    { name: 'Staging (Test)', url: 'https://staging.customer.aku.africa' },
    { name: 'Live (Production)', url: 'https://customer.aku.africa' }
  ];

  const requiredHeaders = [
    { header: 'Content-Type', value: 'application/json', required: true },
    { header: 'Accept', value: 'application/json', required: true },
    { header: 'X-CHANNEL', value: 'CHECK_OUT_WEB', required: true }
  ];

  const statusCodes = [
    { code: 0, status: 'COMPLETED', description: 'Transaction completed successfully.' },
    { code: 1, status: 'PENDING', description: 'Transaction awaiting confirmation.' },
    { code: 2, status: 'AUTHORIZED', description: 'Transaction authorized but not captured.' },
    { code: 3, status: 'CAPTURED', description: 'Funds successfully captured.' },
    { code: 4, status: 'DECLINED', description: 'Transaction declined by processor.' },
    { code: 5, status: 'CANCELED', description: 'Transaction canceled by user.' },
    { code: 6, status: 'REFUNDED', description: 'Transaction refunded to the customer.' },
    { code: 7, status: 'CHARGED_BACK', description: 'Transaction reversed due to chargeback.' },
    { code: 8, status: 'IN_PROGRESS', description: 'Transaction currently processing.' },
    { code: 9, status: 'VOIDED', description: 'Transaction voided.' },
    { code: 10, status: 'PENDING_REVIEW', description: 'Awaiting manual review.' },
    { code: 17, status: 'SCHEDULED', description: 'Future/recurring transaction.' }
  ];

  // Get Transaction Details / Available Payment Methods
  const getTransactionRequestJson = `{
  "reference": "83f09c01-713e-421a-b564-8622fd7b476e"
}`;

  const getTransactionResponseJson = `{
  "responseCode": "00",
  "responseMessage": "Successful",
  "paymentDetail": {
    "merchantId": "merchant-uuid-here",
    "amount": 1000.00,
    "currency": "NGN",
    "itemName": "Product Name",
    "merchantOrderId": "ORDER123456",
    "integratorId": null,
    "itemDescription": "Product description",
    "senderName": "John Doe",
    "email": "johndoe@example.com",
    "phoneNumber": "+2348012345678",
    "status": "PENDING",
    "redirectUrl": "https://yourwebsite.com/callback"
  },
  "integrators": [
    {
      "id": "palmpay-integrator-id",
      "name": "PALMPAY",
      "status": "ACTIVE"
    },
    {
      "id": "opay-integrator-id",
      "name": "OPAY",
      "status": "ACTIVE"
    }
  ]
}`;

  // Check Payment Status
  const checkPaymentStatusResponseJson = `{
  "status": "PENDING",
  "redirectUrl": "https://yourwebsite.com/callback"
}`;

  // Opay API Examples
  const opayCreateResponseJson = `{
  "responseCode": "00",
  "responseMessage": "SUCCESSFUL",
  "status": "COMPLETE",
  "redirectUrl": "https://express.opaycheckout.com/apiCashier/redirect/payment/checkoutHome?orderToken=TOKEN.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
}`;

  // PalmPay API Examples
  const palmpayCreateResponseJson = `{
  "responseCode": "00",
  "responseMessage": "SUCCESSFUL",
  "status": "COMPLETE",
  "redirectUrl": "https://pay.palmpay.com/payment/xyz789abc"
}`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Gateway Checkout</h1>
        
        {/* Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
          <p className="text-gray-500 mb-6 leading-relaxed">
            Aku payment gateway enables merchants to accept payments seamlessly through multiple channels. 
            The system provides reliable APIs for initializing, processing, and verifying payments securely.
          </p>
        </div>

        {/* Supported Payment Methods & Channels */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Supported Payment Methods & Channels</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paymentMethods.map((method, index) => (
                    <tr key={index} className="hover:bg-white">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{method.method}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          method.available 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {method.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Environments */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Environments</h2>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Base URLs</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Environment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {environments.map((env, index) => (
                    <tr key={index} className="hover:bg-white">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{env.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{env.url}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Security Model */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Security Model</h2>

          <p className="text-gray-500 mb-4">
            These are open endpoints that use channel identification and reference-based validation rather than traditional API key authentication.
          </p>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-center mb-3">
              <Shield className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-blue-500">Security Requirements</h3>
            </div>
            <ul className="text-sm text-blue-500/90 space-y-2">
              <li>• All API requests must use HTTPS.</li>
              <li>• <strong>X-CHANNEL header</strong> — Identifies the calling application/channel.</li>
              <li>• <strong>Reference-based validation</strong> — The reference parameter links to a pre-created payment request that was already authenticated upstream.</li>
            </ul>
          </div>
        </div>

        {/* Required Headers */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Required Headers</h2>
          <p className="text-gray-500 mb-4">
            No Authorization header with Bearer token, API key, or secret key is required. All requests must include the following headers:
          </p>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {requiredHeaders.map((item, index) => (
                    <tr key={index} className="hover:bg-white">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{item.header}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{item.value}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-4">Header Example:</h3>
          <div className="mb-6">
            <CodeBlock
              code={`Content-Type: application/json\nAccept: application/json\nX-CHANNEL: CHECK_OUT_WEB`}
              language="text"
            />
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-500 mb-2">Important</h3>
                <p className="text-sm text-yellow-500/90">
                  The reference must be a unique transaction identifier created in a secured context upstream before calling these endpoints.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Get Transaction Details */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Get Transaction Details</h2>
          <p className="text-gray-500 mb-6">
            Before processing a payment, call this endpoint to get transaction details and available payment methods (integrators).
          </p>

          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200">POST</span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 break-all">/payment-gateway/checkout/transaction</code>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Request Body</h4>
            <CodeBlock code={getTransactionRequestJson} language="json" />

            <h4 className="text-md font-semibold text-gray-900 mb-2 mt-4">Response</h4>
            <p className="text-gray-500 mb-4 text-sm">Returns payment details and a list of available integrators (payment methods) with their status.</p>
            <CodeBlock code={getTransactionResponseJson} language="json" />
          </div>
        </div>

        {/* Opay Payment Gateway */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Opay Payment Gateway</h2>
          <p className="text-gray-500 mb-6">
            Use these endpoints to process payments through Opay.
          </p>

          {/* Initiate Opay Payment */}
          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Initiate Opay Payment</h3>
            <p className="text-gray-500 mb-4">Initiates a new payment transaction with Opay. The reference is passed as a query parameter.</p>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200">POST</span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 break-all">/payment-gateway/v1/pay/opay/create?reference=&#123;reference&#125;</code>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Query Parameters</h4>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr className="hover:bg-white">
                    <td className="px-4 py-3 text-sm font-mono text-lime">reference</td>
                    <td className="px-4 py-3 text-sm text-gray-500">string</td>
                    <td className="px-4 py-3 text-sm text-gray-500">The unique transaction reference (UUID)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Response</h4>
            <CodeBlock code={opayCreateResponseJson} language="json" />
          </div>
        </div>

        {/* PalmPay Payment Gateway */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">PalmPay Payment Gateway</h2>
          <p className="text-gray-500 mb-6">
            Use these endpoints to process payments through PalmPay.
          </p>

          {/* Initiate PalmPay Payment */}
          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Initiate PalmPay Payment</h3>
            <p className="text-gray-500 mb-4">Initiates a new payment transaction with PalmPay. The reference is passed as a query parameter.</p>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200">POST</span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 break-all">/payment-gateway/v1/pay/palmpay/create?reference=&#123;reference&#125;</code>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Query Parameters</h4>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr className="hover:bg-white">
                    <td className="px-4 py-3 text-sm font-mono text-lime">reference</td>
                    <td className="px-4 py-3 text-sm text-gray-500">string</td>
                    <td className="px-4 py-3 text-sm text-gray-500">The unique transaction reference (UUID)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Response</h4>
            <CodeBlock code={palmpayCreateResponseJson} language="json" />
          </div>
        </div>

                {/* Check Payment Status */}
                <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Check Payment Status</h2>
          <p className="text-gray-500 mb-6">
            Use this endpoint to check the current status of a payment transaction.
          </p>

          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold tracking-wide bg-blue-50 text-blue-700 border border-blue-200">GET</span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 break-all">/payment-gateway/checkout/status/&#123;reference&#125;</code>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Path Parameters</h4>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr className="hover:bg-white">
                    <td className="px-4 py-3 text-sm font-mono text-lime">reference</td>
                    <td className="px-4 py-3 text-sm text-gray-500">string</td>
                    <td className="px-4 py-3 text-sm text-gray-500">The unique transaction reference (UUID)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-md font-semibold text-gray-900 mb-2">Response</h4>
            <CodeBlock code={checkPaymentStatusResponseJson} language="json" />
          </div>
        </div>

        {/* Payment Flow */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Flow</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <ol className="list-decimal list-inside space-y-3 text-gray-500">
              <li><span className="font-semibold text-gray-900">Initiate</span> — Call <code className="text-lime">/create</code> with transaction reference</li>
              <li><span className="font-semibold text-gray-900">Redirect</span> — User is redirected to gateway portal using <code className="text-lime">redirectUrl</code></li>
              {/* <li><span className="font-semibold text-gray-900">Complete</span> — Redirect user based on final <code className="text-lime">callbackUrl</code></li> */}
              <li><span className="font-semibold text-gray-900">Poll Status</span> — Check status with query params while user completes payment</li>
            </ol>
          </div>
        </div>

        {/* Status Codes */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Codes</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {statusCodes.map((status, index) => (
                    <tr key={index} className="hover:bg-white">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{status.code}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{status.status}</td>
                      <td className="px-4 py-4 text-sm text-gray-500">{status.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export const GatewayCheckout: React.FC = () => {
  return (
    <Routes>
      <Route index element={<GatewayCheckoutHome />} />

    </Routes>
  );
};